import { Injectable } from '@angular/core';
import { Formation, FormationInterface, FormationPayload } from './formation';
import { FormationSearchParamsInterface } from './formation-search-params';
import { BaseModelService } from '@app/abstracts';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { PresenceSummaryResponse } from '@app/models/formation-session/formation-session.service';

import { Helpers } from '@app/shared/helpers';
@Injectable()
export class FormationService extends BaseModelService<
	Formation,
	FormationInterface,
	FormationPayload,
	FormationSearchParamsInterface
> {
	/** @inheritDoc */
	protected path(): string {
		return 'formation';
	}
	/** @inheritDoc */
	protected new(object: FormationInterface): Formation {
		return new Formation(object);
	}

	/** @inheritDoc */
	protected transformSearchParams(
		searchParams: FormationSearchParamsInterface
	): FormationSearchParamsInterface {
		// Search by edge for Start Date
		if (typeof searchParams.start_date__min !== 'undefined') {
			searchParams.start_date__min = Helpers.toMidnightUTC(
				searchParams.start_date__min
			);
		}
		if (typeof searchParams.start_date__max !== 'undefined') {
			searchParams.start_date__max = Helpers.toLastSecondUTC(
				searchParams.start_date__max
			);
		}
		if (typeof searchParams.start_date !== 'undefined') {
			searchParams.start_date__min = Helpers.toMidnightUTC(
				searchParams.start_date
			);
			searchParams.start_date__max = Helpers.toLastSecondUTC(
				searchParams.start_date
			);
			delete searchParams.start_date;
		}
		// Search by edge for End Date
		if (typeof searchParams.end_date__min !== 'undefined') {
			searchParams.end_date__min = Helpers.toMidnightUTC(
				searchParams.end_date__min
			);
		}
		if (typeof searchParams.end_date__max !== 'undefined') {
			searchParams.end_date__max = Helpers.toLastSecondUTC(
				searchParams.end_date__max
			);
		}
		if (typeof searchParams.end_date !== 'undefined') {
			searchParams.end_date__min = Helpers.toMidnightUTC(
				searchParams.end_date
			);
			searchParams.end_date__max = Helpers.toLastSecondUTC(
				searchParams.end_date
			);
			delete searchParams.end_date;
		}
		return searchParams;
	}

	/**
	 * Generate attestation fin de formation for a specific student
	 * @param formationId The ID of the formation
	 * @param payload The payload containing student ID and template
	 * @returns Promise that resolves when generation is complete
	 */
	generateAttestationFinFormation(
		formationId: string,
		payload: {
			stagiaire: string;
			template: string;
			total_time_override?: number;
		}
	): Promise<void> {
		const options = { withCredentials: true };
		return this.http
			.patch<void>(
				`${environment.api.uri}/admin/formation/${formationId}/generate-attestation-fin-de-formation`,
				payload,
				options
			)
			.toPromise();
	}

	/**
	 * Get a presigned URL for downloading an attestation fin de formation document
	 * @param formationId The ID of the formation
	 * @param attestationId The ID of the attestation fin de formation
	 * @param isAdmin Whether the request is made by an admin
	 * @returns Promise that resolves with the presigned URL
	 */
	getPresignedAttestationFinFormation(
		formationId: string,
		attestationId: string,
		isAdmin: boolean = false
	): Promise<{
		url: string;
	}> {
		const prefix = isAdmin ? '/admin' : '';
		const options = { withCredentials: true };

		return this.http
			.get<{
				url: string;
			}>(
				`${environment.api.uri}${prefix}/formation/${formationId}/attestation-fin-de-formation/${attestationId}`,
				options
			)
			.toPromise();
	}

	/**
	 * Get a presigned URL for downloading an attestation fin de formation document as admin
	 * @param formationId The ID of the formation
	 * @param attestationId The ID of the attestation fin de formation
	 * @returns Promise that resolves with the presigned URL
	 */
	getPresignedAttestationFinFormationAsAdmin(
		formationId: string,
		attestationId: string
	): Promise<{
		url: string;
	}> {
		return this.getPresignedAttestationFinFormation(
			formationId,
			attestationId,
			true
		);
	}

	/**
	 * Send an email with the attestation fin de formation to the student
	 * @param formationId The ID of the formation
	 * @param attestationId The ID of the attestation fin de formation
	 * @returns Promise that resolves when the email is sent
	 */
	sendEmailAttestationFinFormation(
		formationId: string,
		attestationId: string
	): Promise<void> {
		const options = { withCredentials: true };

		return this.http
			.post<void>(
				`${environment.api.uri}/admin/formation/${formationId}/attestation-fin-de-formation/${attestationId}/send`,
				{},
				options
			)
			.toPromise();
	}

	/**
	 * Get metadata for a formation including total training time
	 * @param formationId The ID of the formation to get metadata for
	 * @param isAdmin Whether the request is made by an admin
	 * @returns Promise that resolves with the formation metadata
	 */
	getMetadata(
		formationId: string,
		isAdmin: boolean = false
	): Promise<{
		total_hours_harmonized: number;
	}> {
		const prefix = isAdmin ? '/admin' : '';
		const options = { withCredentials: true };

		return this.http
			.get<{ total_hours_harmonized: number }>(
				`${environment.api.uri}${prefix}/formation/${formationId}/metadata`,
				options
			)
			.toPromise();
	}

	/**
	 * Get metadata for a formation as admin
	 * @param formationId The ID of the formation to get metadata for
	 * @returns Promise that resolves with the formation metadata
	 */
	getMetadataAsAdmin(formationId: string): Promise<{
		total_hours_harmonized: number;
	}> {
		return this.getMetadata(formationId, true);
	}

	/**
	 * Get presence summary for a formation including total hours and per-respondant presence
	 * @param formationId The ID of the formation
	 * @param isAdmin Whether the request is made by an admin
	 * @returns Promise that resolves with the presence summary
	 */
	getPresenceSummary(
		formationId: string,
		isAdmin: boolean = false
	): Promise<PresenceSummaryResponse> {
		const prefix = isAdmin ? '/admin' : '';
		const options = { withCredentials: true };

		return this.http
			.get<PresenceSummaryResponse>(
				`${environment.api.uri}${prefix}/formation/${formationId}/presence`,
				options
			)
			.toPromise();
	}

	/**
	 * Get presence summary for a formation as admin
	 * @param formationId The ID of the formation
	 * @returns Promise that resolves with the presence summary
	 */
	getPresenceSummaryAsAdmin(
		formationId: string
	): Promise<PresenceSummaryResponse> {
		return this.getPresenceSummary(formationId, true);
	}
}
